import { Tag } from 'antd';
import type { LifecycleStatus } from '@/types/api';
import { colorTokens } from '@theme/tokens';

const labels: Record<LifecycleStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
  scheduled: 'Scheduled',
  activated: 'Activated',
  published: 'Published',
  suspended: 'Suspended',
  archived: 'Archived',
};

const colorMap: Record<LifecycleStatus, { color: string; bg: string }> = {
  draft: { color: colorTokens.draft, bg: colorTokens.draftBg },
  submitted: { color: colorTokens.submitted, bg: colorTokens.submittedBg },
  under_review: { color: colorTokens.underReview, bg: colorTokens.underReviewBg },
  approved: { color: colorTokens.approved, bg: colorTokens.approvedBg },
  rejected: { color: colorTokens.rejected, bg: colorTokens.rejectedBg },
  scheduled: { color: colorTokens.scheduled, bg: '#f9f0ff' },
  activated: { color: colorTokens.published, bg: '#e6fffb' },
  published: { color: colorTokens.published, bg: '#e6fffb' },
  suspended: { color: colorTokens.suspended, bg: '#fff2e8' },
  archived: { color: colorTokens.archived, bg: colorTokens.archivedBg },
};

interface LifecycleBadgeProps {
  status: LifecycleStatus;
  version?: number;
  size?: 'small' | 'default';
}

/**
 * Universal lifecycle badge. Used everywhere a record has a state.
 * See docs/ui-ux-design.md §3.2.
 */
export function LifecycleBadge({ status, version, size = 'default' }: LifecycleBadgeProps) {
  const { color, bg } = colorMap[status];
  return (
    <Tag
      color={color}
      style={{
        background: bg,
        color,
        border: `1px solid ${color}33`,
        fontWeight: 500,
        fontSize: size === 'small' ? 11 : 12,
        padding: size === 'small' ? '0 6px' : '2px 8px',
        borderRadius: 4,
      }}
    >
      <span style={{ marginRight: 4 }}>●</span>
      {labels[status]}
      {version !== undefined && <span style={{ marginLeft: 6, opacity: 0.7 }}>v{version}</span>}
    </Tag>
  );
}